import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { errMsg, FIELD_LABELS, fmt, useApi, useLoad, useTaxonomy } from "../../lib/api";
import { Empty, ErrorBox, Loading, OrganicTag, PageTitle, ProtectedTag, StatusBadge } from "../../components/kit";
import { Sponsored } from "../../components/Sponsored";
import { ProviderCard } from "../Directory";
import { RequestForm } from "./RequestForm";
import { RequestTimeline } from "./RequestTimeline";
import { RatingPanel } from "../../components/RatingPanel";

function Matches({ d }: { d: any }) {
  const { areaName } = useTaxonomy();
  const cands = d.match?.candidates || [];
  const introFor = (cid: string) => d.introductions.find((i: any) => i.candidate_id === cid);
  return (
    <section className="organic-zone" aria-labelledby="matches-h" data-testid="organic-matches-section">
      <h2 id="matches-h" className="text-2xl font-extrabold text-[var(--navy)]">We Found Great Matches For You</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">Based on your request, here are approved providers ready to help. Names stay private until you engage one.</p>
      <div className="mb-3"><OrganicTag text="Organic · fit + fair rotation · ads & plans excluded" /></div>
      {cands.length === 0 && <Empty title="No eligible providers yet">No approved provider covers this trade and area with availability right now.</Empty>}
      <ol className="space-y-3">
        {cands.map((c: any) => {
          const intro = introFor(c.candidate_id);
          return (
            <ProviderCard key={c.candidate_id} v={c} areaName={areaName} testId={`match-candidate-${c.candidate_id}`}
              to={`/customer/requests/${d.request.id}/candidates/${c.candidate_id}`}
              extra={intro ? (intro.request_version !== d.request.version ? <span className="badge badge-red" data-testid={`match-reconsent-${c.candidate_id}`}>consent needed</span> : <StatusBadge value={intro.status} testId={`match-intro-status-${c.candidate_id}`} />) : c.engaged ? <span className="badge badge-blue">engaged</span> : null} />
          );
        })}
      </ol>
    </section>
  );
}

function Consents({ d, reload }: { d: any; reload: () => void }) {
  const api = useApi();
  const revoke = async (id: string) => {
    try { await api("post", `/customer/consents/${id}/revoke`); toast.success("Consent revoked"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (d.consents.length === 0) return <Empty title="No consents granted yet" />;
  return (
    <ul className="space-y-2" data-testid="consents-list">
      {d.consents.map((c: any) => (
        <li key={c.id} className="card text-sm" data-testid={`consent-row-${c.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <strong>{c.org_name}</strong>
            <div className="flex items-center gap-2"><span className="mono text-xs">v{c.request_version}</span><StatusBadge value={c.status} testId={`consent-status-${c.id}`} /></div>
          </div>
          <p className="mt-1">Fields: {c.fields.map((f: string) => FIELD_LABELS[f]).join(", ")}</p>
          <p className="text-[var(--muted)]">Purpose: {c.purpose} · expires {fmt(c.expires_at)}</p>
          {c.status === "granted" && <button className="btn btn-sm btn-danger mt-2" onClick={() => revoke(c.id)} data-testid={`consent-revoke-button-${c.id}`}>Revoke</button>}
        </li>
      ))}
    </ul>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const api = useApi();
  const { catName, areaName } = useTaxonomy();
  const { data: d, loading, error, reload } = useLoad<any>(`/customer/requests/${id}`);
  const [editing, setEditing] = useState(false);
  if (loading && !d) return <div className="mx-auto max-w-5xl px-4"><Loading /></div>;
  if (error) return <div className="mx-auto max-w-5xl px-4 py-10"><ErrorBox message={error === "Not found" ? "Request not found." : error} onRetry={reload} /></div>;
  if (!d) return null;
  const r = d.request;
  const save = async (v: any) => {
    try {
      const res = await api("patch", `/customer/requests/${id}`, v);
      toast[res.material_change ? "warning" : "success"](res.material_change ? "Material change saved: existing consents were voided. Approve recipients again to share." : "Request updated");
      setEditing(false); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this request? All active consents will be revoked.")) return;
    try { await api("post", `/customer/requests/${id}/cancel`); toast.success("Request cancelled"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto max-w-5xl px-4 pb-28 pt-10 lg:pb-10">
      <Link to="/customer" className="text-sm font-semibold text-[var(--blue)] underline" data-testid="request-back-link">← My requests</Link>
      <PageTitle kicker={`${catName(r.category_id)} · ${areaName(r.area_id)}`} title={r.title}>
        <div className="flex items-center gap-2"><span className="mono text-xs" data-testid="request-version">v{r.version}</span><StatusBadge value={r.status} testId="request-status" /></div>
      </PageTitle>
      <RequestTimeline d={d} />
      {editing ? (
        <div className="mb-8"><RequestForm initial={r} submitLabel="Save changes" onSubmit={save} onCancel={() => setEditing(false)} /></div>
      ) : (
        <div className="card mb-8 space-y-3" data-testid="request-summary-card">
          <p className="text-sm">{r.summary || <em className="text-[var(--muted)]">No summary</em>}</p>
          <div className="protected-zone text-sm" data-testid="request-private-fields">
            <ProtectedTag text="Your protected details" />
            <dl className="mt-2 grid gap-1 sm:grid-cols-2">
              {Object.keys(FIELD_LABELS).map((f) => <div key={f}><dt className="inline font-semibold">{FIELD_LABELS[f]}: </dt><dd className="inline">{r.private[f] || "—"}</dd></div>)}
            </dl>
          </div>
          {r.status === "open" && (
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)} data-testid="request-edit-button">Edit request</button>
              <button className="btn btn-danger btn-sm" onClick={cancel} data-testid="request-cancel-button">Cancel request</button>
            </div>
          )}
        </div>
      )}
      <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
        <div className="space-y-10">
          <Matches d={d} />
          {d.introductions.some((i: any) => i.status === "accepted") && (
            <section aria-labelledby="ratings-h" className="space-y-3" data-testid="customer-ratings-section">
              <h2 id="ratings-h" className="text-2xl font-bold">Completed work & ratings</h2>
              <p className="text-sm text-[var(--muted)]">Completion needs confirmation from you and the provider. Closing or cancelling a request doesn't count as completion. Ratings are stars only, and your score stays private between you, this provider and Verge operations.</p>
              {d.introductions.filter((i: any) => i.status === "accepted").map((i: any) => <RatingPanel key={i.id} side="customer" introId={i.id} title={i.org_name || "Provider"} />)}
            </section>
          )}
          <section aria-labelledby="consents-h">
            <h2 id="consents-h" className="mb-3 text-2xl font-bold">Consents</h2>
            <Consents d={d} reload={reload} />
          </section>
        </div>
        <Sponsored placement="request" categoryId={r.category_id} />
      </div>
    </div>
  );
}
